import { PaginationControl } from '@common/components/PaginationControl';
import { SearchBar } from '@common/components/SearchBar';
import { Title } from '@common/components/Title';
import { usePaginationControls } from '@common/hooks';
import { Layout } from '@common/layouts';
import { Collection, useCollections } from '@features/Collection';
import { CollectionCard } from '@pages/Home/cards/CollectionCard';
import { AVENUE_CONFIGS } from '@pages/Home/constants/avenueConfigs';
import { CaretLeftIcon } from '@radix-ui/react-icons';
import { Box, Button, Flex, ScrollArea, Text } from '@radix-ui/themes';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ExtendedCollectionsUI } from './ExtendedCollectionsUI';

const pageSizes = [...Array(5)].map((_, i) => (i + 1) * 30);

export const ExtendedCollectionsPage = () => {
    const { t } = useTranslation('home');
    const navigate = useNavigate();

    const { title = 'all' } = useParams<{ title: string }>();
    const configKey = (
        title in AVENUE_CONFIGS ? title : 'all'
    ) as keyof typeof AVENUE_CONFIGS;
    const baseConfig = AVENUE_CONFIGS[configKey];

    const {
        data,
        isLoading,
        itemCount,
        pageCount,
        paginationState,
        searchState,
    } = useCollections({
        pageIndex: 0,
        pageSize: pageSizes[0],
        ...baseConfig,
    });

    const paginationControls = usePaginationControls({
        itemCount,
        pageSizes,
        ...paginationState,
    });

    const renderCollectionItem = (collection: Collection): React.ReactNode => {
        return <CollectionCard collection={collection} />;
    };

    return (
        <Layout.Content overflow={'hidden'}>
            <Flex direction={'column'} gap={'3'} p={'3'}>
                <Box>
                    <Button
                        color='gray'
                        variant='ghost'
                        onClick={() => {
                            navigate(-1);
                        }}
                    >
                        <CaretLeftIcon />
                        {t('common:buttons.back')}
                    </Button>
                </Box>

                <Flex gap={'3'} align={'baseline'}>
                    <Title mb={'0'}>{t(title)}</Title>

                    <Text size={'1'} color='gray'>
                        <Trans
                            i18nKey={'common:parenthesis'}
                            values={{ value: itemCount }}
                        />
                    </Text>
                </Flex>

                <Box width={'500px'}>
                    <SearchBar
                        name='collection'
                        placeholder={t(
                            'home:extendedCollection.search.placeholder',
                        )}
                        mb={'4'}
                        {...searchState}
                    />
                </Box>
            </Flex>

            <ScrollArea>
                <Box py={'3'}>
                    <ExtendedCollectionsUI<Collection>
                        data={data}
                        totalCount={itemCount}
                        isLoading={isLoading}
                        renderItem={renderCollectionItem}
                    />
                </Box>
            </ScrollArea>

            <PaginationControl
                isPaginated={pageCount > 1}
                {...paginationControls}
            />
        </Layout.Content>
    );
};
